import * as React from 'react'
import { LnRouterTableDefinition } from '../node/channelList/table/data/LnRouterTableDefinition';
import { useUserPlan } from '../auth/plans/useUserPlan';
import { LnRouterTableColumn } from '../node/channelList/table/data/LnRouterTableColumn';
import Link from 'next/link';
import NodePubkey from '../general/NodePubkey';
import InternalNodeRankingTable from './InternalNodeRankingTable';
import FormatSatoshi from '../general/FormatSatoshi';
import ActionButton from '../general/ActionButton';
import LoadingSpinner from '../general/LoadingSpinner';
import { useWindowSize } from '../general/useWindowSize';
import { INodeInfo } from '../node/INodeInfo';
import { useSubscriberRankingNodes } from './useSubscriberRankingNodes';
import { InView } from 'react-intersection-observer';
import { Tooltip } from '@mui/material';
import IsPeerOfMeGuard from '../node/isMyPeer/IsPeerOfMeGuard';


type Props = {
  search?: string
}

function useNodeTable(): [() => any, LnRouterTableDefinition<INodeInfo>] {
  const windowSize = useWindowSize()
  const isDesktop = windowSize.width > 800;


  function generateTable() {
    return new LnRouterTableDefinition<INodeInfo>([
      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Routing Score <br/>
        Percentile <span className='text-gray-400 text-sm'>(Rank)</span></span>,
        'liquidity.routingScorePercentile',
        props => <div className=''>
          <div className="text-xl">
            <span>{props.value}th</span>
            <span className='text-sm ml-1 text-gray-400'>
              ({props.row.original.liquidity.routingScoreRank})
            </span>
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: true }
      ),
      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Node</span>,
        'label',
        props => <div>
          <div className="font-semibold text-xl md:text-normal truncate max-w-[10rem] lg:max-w-[20rem]">
            <Link href={'/node/' + props.row.original.id}>
              <a className='hover:text-viking-700' target="_blank">
                {props.value}
              </a>
            </Link>
          </div>
          <div className="text-2xs text-gray-400">
            <NodePubkey nodeId={props.row.original.id} allowCopy={true} />
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left' }
      ),
      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Capacity</span>,
        'capacitySat',
        props => <div>
          <div className="text-sm">
            ₿<FormatSatoshi satoshi={props.value} decimals={2} />
          </div>
          <div className="text-2xs text-gray-500">
            {props.row.original.channelCount} channels
          </div>
          <div className="text-2xs text-gray-500">
            {props.row.original.medianPpm}ppm median
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),

      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Response time</span>,
        'liquidity.avgOnionResponseTime',
        props => <div>
          <div className="text-sm">
            {props.value}s
          </div>
          <div className="text-2xs text-gray-500">
            Channel Average
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),

      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Balanced</span>,
        'liquidity.balancedPercent',
        props => <div>
          <div className="text-sm">
            {Math.round(props.value * 100)}%
          </div>
          <div className="text-2xs text-gray-500">
            of all Channels
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),

      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Hops to network</span>,
        'liquidity.adjustedClosenessCentrality',
        props => <div>
          <div className="text-sm">
            {Math.round((1 / props.value) * 100) / 100}
          </div>
          <div className="text-2xs text-gray-500">
            Liquidity adjusted
          </div>
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),

      new LnRouterTableColumn<INodeInfo>(
        <span className='font-light'>Features</span>,
        'ipAddress',
        props => <div className='grid grid-cols-1 lg:grid-cols-3 gap-1 text-xs pr-2'>

          {props.row.original.ipAddress &&
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              placement='bottom' title={
                <div className='p-2 text-sm font-normal'>Allow connections via IP.</div>
              }>
              <div className='font-bold p-[1px] rounded-full border border-gray-400 text-center'>
                IP
              </div>
            </Tooltip>
          }

          {props.row.original.torAddress &&
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              placement='bottom' title={
                <div className='p-2 text-sm font-normal'>Allows connections via TOR.</div>
              }>
              <div className='font-bold py-[1px] px-[4px] rounded-full border border-gray-400 text-center'>
                TOR
              </div>
            </Tooltip>
          }
          <IsPeerOfMeGuard peerId={props.row.original.id}>
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              placement='bottom' title={
                <div className='p-2 text-sm font-normal'>You share a channel with this node.</div>
              }>
              <div className='font-bold p-[1px] rounded-full border dark:border-white border-transparent 
              bg-black dark:bg-white text-white dark:text-black text-center'>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"
                  className='inline-block'>
                  <path d="M6.5 11.25V12H7.5V4H6.5V4.75H3.75V7H0V9H3.75V11.25H6.5Z" fill="currentColor" />
                  <path d="M9.5 4.75V4H8.5V12H9.5V11.25H12.25V9H16V7H12.25V4.75H9.5Z" fill="currentColor" />
                </svg>
              </div>
            </Tooltip>
          </IsPeerOfMeGuard>
          {props.row.original.liquidityAd &&
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              placement='bottom' title={
                <div className='p-2 text-sm font-normal'>Node provides a Liquidity Ad.</div>
              }>
              <div className='py-[1px] rounded-full border border-gray-400 text-center'>
                <svg className='inline-block'
                 xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 2 8 12" fill="currentColor">
                  <path d="M8 8C8 10.2091 6.20914 12 4 12C1.79086 12 0 10.2091 0 8C0 5.79086 4 1.33333 4 0C4 1.33333 8 5.79086 8 8Z" fill="currentColor" />
                  <path d="M6.66667 8C6.66667 9.10457 5.10457 10.6667 4 10.6667" stroke="white" stroke-width="0.3" />
                </svg>
              </div>
            </Tooltip>
          }
          {props.row.original.features.zeroBaseFee &&
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={5000}
              placement='bottom' title={
                <div className='p-2 text-sm font-normal'>#zeroBaseFee</div>
              }>
              <div className='font-bold py-[1px] px-[4px] rounded-full border border-gray-400 text-center'>
                ZBF
              </div>
            </Tooltip>
          }
        </div>,
        { headerGroup: '', headerAlign: 'left', enabled: isDesktop }
      ),

    ])
  }

  const [table, setTable] = React.useState(generateTable());

  function forceUpdate() {
    setTable(table.copy());
  }


  React.useEffect(() => {
    setTable(generateTable())
  }, [windowSize?.width])

  return [forceUpdate, table];
}


const NodeRankingTable: React.FC<Props> = ({ search }: Props) => {
  const limit = 20;
  const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);
  const [forceUpdate, table] = useNodeTable();
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  const [skip, setSkip] = React.useState<number>(0);
  const query = useSubscriberRankingNodes(0, limit, debouncedSearch);
  const elements = query.data?.elements || [];
  const rowsCount = query.data?.count || 0;



  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSkip(0); // Reset skip on amountSat change
      setDebouncedSearch(search);
    }, 1000)

    return () => clearTimeout(delayDebounceFn)

  }, [search]);


  async function loadMore() {
    const newSkip = skip + limit;
    setSkip(newSkip);
    setIsLoadingMore(true);
    await query.fetchAdditional(newSkip, debouncedSearch);
    setIsLoadingMore(false);
  }

  const loading = query.loading || search !== debouncedSearch;


  return (
    <div>
      <div className="flex justify-end pb-4 pt-2">
        {loading &&
          <LoadingSpinner width={100} height={20} color="#4adddf" />
        }
        {!loading &&
          <div style={{ height: '20px' }} />
        }

      </div>
      <InternalNodeRankingTable
        className='w-full'
        definition={table} rows={elements} skip={skip} limit={limit} rowsCount={rowsCount}
        onUserInteraction={function (skip: number, limit: number) {
          loadMore();
        }} />
      {elements.length < rowsCount &&
        <div className='pt-8'>
          <InView onChange={(inview, entry) => {
            if (!inview) {
              return;
            }
            if (skip < 100) {
              loadMore();
            }

          }}>
            <ActionButton className="w-full" isLoading={isLoadingMore} onClick={() => {
              loadMore();
            }}>
              Load more
            </ActionButton>
          </InView>
        </div>
      }

      {rowsCount === 0 && !query.loading &&
        <div className='pt-4'>
          <p className='text-center text-xl'>No node found.</p>
        </div>
      }
    </div>

  );
}

export default NodeRankingTable;

